import React, { useImperativeHandle, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { SubmitHandler, useForm } from 'react-hook-form';

import ConfirmationDialog from 'ui/shared/ConfirmationDialog';

import { TaskCodeSchema } from '../../constants/schemas';
import { TaskClosingCodesTemplate } from '../../constants/TaskClosingCodesTemplate';
import { TaskClosingCode } from '../../types/interfaces';

const initState: TaskClosingCode = TaskClosingCodesTemplate;

const defaultTaskCodeChoiseProps: TaskCodeChoiseProps = {
  taskCodeChoiseProps: { code: initState },
  closingCodes: []
};

interface TaskCodeChoiseProps {
  taskCodeChoiseProps?: { code: TaskClosingCode }; // Make it optional
  closingCodes: TaskClosingCode[];
  onAdd?: (updatedData: TaskClosingCode) => void;
  closeDialog?: () => void;
  saveDialog?: () => void;
  ref?: React.RefObject<RefInterface | null>;
}

interface RefInterface {
  handleCancel: () => void;
}

const TaskCodeChoiseCard = React.forwardRef<RefInterface, TaskCodeChoiseProps>(
  (
    {
      taskCodeChoiseProps = defaultTaskCodeChoiseProps.taskCodeChoiseProps,
      onAdd,
      closeDialog,
      saveDialog,
      closingCodes
    }: TaskCodeChoiseProps,
    ref: React.Ref<RefInterface>
  ) => {
    const {
      handleSubmit,
      watch,
      trigger,
      setValue,
      formState: { isDirty, errors }
    } = useForm({
      resolver: zodResolver(TaskCodeSchema),
      defaultValues: taskCodeChoiseProps
    });

    const [showDialog, setShowDialog] = useState(false);
    const toast = useRef<Toast>(null);
    const taskClosingCode = watch('code');

    const onSubmit: SubmitHandler<{ code: TaskClosingCode }> = (formData) => {
      if (onAdd) {
        onAdd(formData.code);
      }
      saveDialog && saveDialog();
    };

    const handleCancel = () => {
      if (isDirty) {
        setShowDialog(true);
      } else if (closeDialog) {
        closeDialog();
      }
    };

    useImperativeHandle(ref, () => ({
      handleCancel
    }));

    const handleConfirmationContinue = () => {
      setShowDialog(false);
      if (closeDialog) {
        closeDialog();
      }
    };

    const handleConfirmationCancel = () => {
      setShowDialog(false);
    };

    return (
      <div className="dialog-tabless-card dialog-tabless-card--fixed-footer">
        <Toast ref={toast} />
        <div className="gap-3 pb-0">
          <Divider />
          <div className="tasktype-card__column">
            <div className="flex flex-column">
              <div className="flex flex-row gap-5">
                <div className="w-full">
                  <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                    <Dropdown
                      value={taskClosingCode?.id ? taskClosingCode : null}
                      options={closingCodes}
                      onChange={(e) => {
                        setValue('code', e.value ? e.value : initState);
                        trigger('code');
                      }}
                      optionLabel="taskClosingCode"
                      placeholder="Выберите шифр закрытия наряда"
                      filter
                      showClear
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
              <p
                className={` card-errors ${
                  errors.code ? 'text-red-500' : 'text-transparent'
                } `}
              >
                {errors.code && errors?.code?.taskClosingCode
                  ? errors?.code?.taskClosingCode.message
                  : ' '}
              </p>
            </div>
          </div>
        </div>

        <div className="default-card__buttons">
          <div className="flex gap-3">
            <Button
              label="Сохранить"
              icon="pi pi-check"
              onClick={handleSubmit(onSubmit)}
            />
            <Button
              label="Отмена"
              icon="pi pi-times"
              onClick={handleCancel}
              className="p-button-secondary"
            />
          </div>
        </div>

        <ConfirmationDialog
          visible={showDialog}
          onCancel={handleConfirmationCancel}
          onContinue={handleConfirmationContinue}
        />
      </div>
    );
  }
);

TaskCodeChoiseCard.displayName = 'TaskCodeChoiseCard';

export default TaskCodeChoiseCard;
